/* global document, Office */

// Ce fichier reste le "moteur" central du taskpane : chargement du mail courant, génération/édition/
// insertion de la réponse IA, cache de brouillon, feedback, et le bootstrap Office.onReady qui câble
// tout le reste. Les écrans et widgets périphériques ont été extraits dans des modules dédiés (voir
// les imports ci-dessous) -- ce qui reste ici est ce qui touche directement à l'état de génération
// (isGeneratingReply, replyMode, le cache par mail) et n'aurait pas pu être séparé sans complexifier
// inutilement le partage de cet état.
import { acquireGraphToken, getConversationMessages } from "./graph";
import { mailData, setMailData } from "./mailData";
import { CachedReplyState, clearCachedReply, loadCachedReply, saveCachedReply } from "./replyCache";
import {
  generateReply,
  PraetoriaCancelledError,
  PraetoriaTimeoutError,
  ReplyThreadMessage,
  ServiceUnavailableError,
  submitReplyEdit,
  submitUserReview,
} from "./praetoria";
import { bindActivate, describeActionError, errorMessage, openDialogPage } from "./ui/domHelpers";
import { applyTheme, getStoredTheme } from "./ui/theme";
import { autoResizeInstruction, bindInstructionInput, bindInstructionPlaceholderMarquee } from "./ui/instructionInput";
import { bindReplyTextBlur, bindReplyTextHighlight, renderReplyHighlight } from "./ui/replyHighlight";
import { handleDictateInstruction, isDictationRecording, stopDictationIfRecording } from "./ui/dictation";
import { initUserInfoDropdowns, showUserInfoPage } from "./pages/userInfoPage";
import { initThemeDropdown, showSettingsPage } from "./pages/settingsPage";
import { handleAuthRetry, handleCguAccept, handleCguCheckboxChange, runLicenseCheck } from "./pages/licenseGate";
import { loadAttachments, resetAttachmentsPanel, showAttachmentsPage } from "./pages/attachmentsPage";

let isGeneratingReply = false;
let generationAbortController: AbortController | null = null;
// Incrémenté à chaque nouvelle génération et à chaque reset (Recharger). Permet au bloc `finally`
// d'une génération annulée/périmée de savoir si une génération plus récente a déjà démarré avant
// de toucher l'état partagé (isGeneratingReply, generationAbortController) -- sinon son `finally`
// tardif peut s'exécuter après coup et écraser l'état de la génération suivante.
let generationId = 0;
let replyFeedback: "up" | "down" | null = null;
let isSubmittingFeedback = false;
let originalAiReply: string | null = null;
let lastSentEditedReply: string | null = null;
let replySessionId: string = generateSessionId();
// Module de génération à l'origine de la réponse affichée -- toujours null désormais
// (seul /email/reply reste actif), transmis tel quel à /email/user_review et
// /email/reply_edit pour trier la moisson des retours par module (spec du 19/09/2026).
let currentModule: string | null = null;
let currentVersionModule: string | null = null;

// Clé du mail actuellement affiché, utilisée pour retrouver/sauvegarder sa réponse en cache
// (voir replyCache.ts). Le taskpane étant rechargé en entier à chaque changement de mail, ce
// n'est qu'au (re)chargement suivant qu'on peut restaurer ce qui était en cours.
let currentItemKey: string | null = null;
let instructionCachePersistTimer: number | undefined;

// Identifie une ouverture du volet sur un mail donné, pour que le back puisse distinguer deux
// sessions de travail rapprochées sur le même mail (un simple timestamp ne suffit pas côté serveur).
// crypto.randomUUID n'existe pas sur les vieux Outlook/Trident encore supportés (voir polyfills IE11
// du projet) -- repli sur un générateur RFC4122 v4 maison dans ce cas.
function generateSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

applyTheme(getStoredTheme());

// showAppBody() peut être rappelée plusieurs fois dans une même session (retry après erreur
// d'identité, acceptation CGU) -- ce flag évite de réempiler des addEventListener/ResizeObserver
// à chaque appel (bindActivate, lui, réassigne .onclick donc n'a pas ce problème).
let appBodyListenersBound = false;

function showAppBody(): void {
  document.getElementById("license-error").style.display = "none";
  document.getElementById("identity-error").style.display = "none";
  document.getElementById("technical-error").style.display = "none";
  document.getElementById("service-unavailable").style.display = "none";
  document.getElementById("cgu-required").style.display = "none";
  document.getElementById("settings-page").style.display = "none";
  document.getElementById("user-info-page").style.display = "none";
  document.getElementById("attachments-page").style.display = "none";
  document.getElementById("app-body").style.display = "flex";
  bindActivate("reload", displayMailContent);
  bindActivate("generate-reply", handleGenerateButtonClick);
  bindActivate("insert-reply", handleInsertReply);
  bindActivate("feedback-up", () => handleReplyFeedback("up"));
  bindActivate("feedback-down", () => handleReplyFeedback("down"));
  bindActivate("reply-feedback-submit", () => void handleSubmitReplyFeedback());
  bindActivate("clear-reply", handleClearReply);  bindActivate("edit-reply", handleToggleEditReply);
  bindActivate("dictate-instruction", handleDictateInstruction);
  initUserInfoDropdowns();
  initThemeDropdown();
  bindActivate("settings", () => void showSettingsPage(resetReplyPanel));
  bindActivate("user-info", () => void showUserInfoPage());
  bindActivate("attachments-button", showAttachmentsPage);
  bindActivate("show-cgu", () => openDialogPage("cgu.html"));
  bindActivate("show-tutorial", () => openDialogPage("tutoriel.html"));
  bindActivate("bracket-warning-cancel", hideBracketWarning);
  bindActivate("bracket-warning-confirm", () => {
    hideBracketWarning();
    insertReplyIntoMail();
  });
  bindActivate("info-popup-ok", hideInfoPopup);
  if (!appBodyListenersBound) {
    appBodyListenersBound = true;
    bindInstructionInput(() => void handleGenerateReply(), scheduleInstructionCachePersist);
    bindReplyTextBlur(persistReplyCache);
    bindReplyTextHighlight();
    bindInstructionPlaceholderMarquee();
  }
  // Filet de sécurité : si l'utilisateur clique sur un autre mail alors qu'une saisie de consigne
  // est encore en attente de sauvegarde (debounce non écoulé), on force la sauvegarde avant que le
  // taskpane ne soit détruit par le rechargement complet.
  window.addEventListener("pagehide", persistReplyCache);
  window.addEventListener("unload", persistReplyCache);
  displayMailContent();
}

// Envoi silencieux au back au clic sur "Insérer", uniquement si le notaire a modifié la réponse
// proposée -- ne bloque jamais l'UI et ne remonte aucune erreur à l'utilisateur (contrairement au
// vote pouce, cette collecte est passive et n'a pas de retour visuel).
async function reportReplyEditIfNeeded(): Promise<void> {
  if (!originalAiReply || !mailData.graphToken) {
    return;
  }
  const currentReply = (document.getElementById("reply-text") as HTMLTextAreaElement).value;
  if (currentReply === originalAiReply || currentReply === lastSentEditedReply) {
    return;
  }
  const subjectAtCapture = mailData.subject;
  const graphTokenAtCapture = mailData.graphToken;
  const sessionIdAtCapture = replySessionId;
  const moduleAtCapture = currentModule;
  const versionModuleAtCapture = currentVersionModule;
  try {
    await submitReplyEdit(
      graphTokenAtCapture,
      subjectAtCapture,
      originalAiReply,
      currentReply,
      sessionIdAtCapture,
      moduleAtCapture,
      versionModuleAtCapture
    );
    lastSentEditedReply = currentReply;
  } catch {
    // Best-effort : on ne redonne pas la main à l'utilisateur pour ça. lastSentEditedReply n'est
    // pas mis à jour, donc une nouvelle perte de focus (même sans frappe supplémentaire) retentera.
  }
}

function handleItemChanged(): void {
  // Sur certains clients (manifest XML legacy, panneau épinglé), le taskpane n'est pas détruit et
  // recréé quand l'utilisateur sélectionne un autre mail -- Office.context.mailbox.item change sous
  // le capot sans que le reste de notre état (mailData, réponse générée, cache) ne suive. On force
  // le même rafraîchissement complet que le bouton "Recharger" pour rester synchro avec le mail réel.
  if (document.getElementById("app-body").style.display !== "none") {
    void displayMailContent();
  }
}

Office.onReady(async (info) => {
  if (info.host === Office.HostType.Outlook) {
    document.getElementById("sideload-msg").style.display = "none";
    bindActivate("license-retry", () => void handleAuthRetry(showAppBody));
    bindActivate("identity-retry", () => void handleAuthRetry(showAppBody));
    bindActivate("technical-retry", () => void handleAuthRetry(showAppBody));
    bindActivate("service-unavailable-retry", () => void handleAuthRetry(showAppBody));
    bindActivate("cgu-required-accept", () => void handleCguAccept(showAppBody));
    document.getElementById("cgu-required-checkbox").addEventListener("change", handleCguCheckboxChange);
    document.getElementById("cgu-required-read-link").onclick = (event) => {
      event.preventDefault();
      openDialogPage("cgu.html");
    };
    Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, handleItemChanged, (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        console.error("Impossible de s'abonner aux changements de mail", result.error);
      }
    });
    await runLicenseCheck(showAppBody);
  }
});

function getBodyText(item: Office.MessageRead): Promise<string> {
  return new Promise((resolve, reject) => {
    item.body.getAsync(Office.CoercionType.Text, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(result.error);
      }
    });
  });
}

// En mode réponse/compose, item.subject et item.from sont des objets asynchrones
// (Office.Subject / Office.From), pas des string/EmailAddressDetails comme en lecture.
// En composition d'un nouveau mail en particulier, item.subject est un SubjectField async
// (getAsync) -- sans cette distinction, mailData.subject reçoit l'objet SubjectField au lieu
// du texte, d'où l'erreur de subject sur un nouveau mail.
function resolveSubjectText(item: Office.MessageRead): Promise<string> {
  const subject = item.subject as unknown;
  if (typeof subject === "string") {
    return Promise.resolve(subject);
  }
  return new Promise((resolve) => {
    (subject as Office.Subject).getAsync((result) => {
      resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value || "" : "");
    });
  });
}

function resolveFromText(item: Office.MessageRead): Promise<string> {
  const from = item.from as unknown;
  if (from && typeof from === "object" && "displayName" in from) {
    const details = from as Office.EmailAddressDetails;
    return Promise.resolve(`${details.displayName} <${details.emailAddress}>`);
  }
  if (from && typeof (from as Office.From).getAsync === "function") {
    return new Promise((resolve) => {
      (from as Office.From).getAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
          resolve(`${result.value.displayName} <${result.value.emailAddress}>`);
        } else {
          resolve("");
        }
      });
    });
  }
  return Promise.resolve("");
}

async function loadThread(item: Office.MessageRead): Promise<void> {
  if (!mailData.graphToken) {
    return;
  }

  try {
    mailData.thread = await getConversationMessages(item.conversationId, mailData.graphToken);
  } catch (error) {
    console.error("Impossible de charger l'historique de la conversation", error);
  }
}

function buildReplyThread(): ReplyThreadMessage[] {
  if (mailData.thread.length > 0) {
    return mailData.thread.map((message) => ({
      sender: message.from,
      date: message.receivedDateTime,
      body: message.bodyText,
    }));
  }

  return [
    {
      sender: mailData.from,
      date: mailData.date,
      body: mailData.body,
    },
  ];
}

function resetReplyPanel(): void {
  const statusElement = document.getElementById("reply-status");
  const textElement = document.getElementById("reply-text") as HTMLTextAreaElement;
  const instructionElement = document.getElementById("reply-instruction") as HTMLTextAreaElement;
  const dictateStatusElement = document.getElementById("dictate-status");
  generationAbortController?.abort();
  generationAbortController = null;
  generationId++;
  stopDictationIfRecording();
  resetAttachmentsPanel();
  dictateStatusElement.textContent = "";
  dictateStatusElement.className = "reply-status";
  statusElement.textContent = "";
  statusElement.className = "reply-status";
  textElement.value = "";
  renderReplyHighlight();
  instructionElement.value = "";
  autoResizeInstruction();
  setGenerateButtonLoading(false);
  setReplyEditable(false);
  document.getElementById("insert-reply").classList.add("is-hidden");
  document.getElementById("reply-feedback").classList.add("is-hidden");
  resetReplyFeedback();
  originalAiReply = null;
  lastSentEditedReply = null;
  currentModule = null;
  currentVersionModule = null;
  replySessionId = generateSessionId();
  isGeneratingReply = false;
  instructionElement.focus();
}

function persistReplyCache(): void {
  if (!currentItemKey) {
    return;
  }
  const textElement = document.getElementById("reply-text") as HTMLTextAreaElement;
  const instructionElement = document.getElementById("reply-instruction") as HTMLTextAreaElement;
  const commentElement = document.getElementById("reply-feedback-comment") as HTMLTextAreaElement;
  const hasReply = !document.getElementById("insert-reply").classList.contains("is-hidden");

  if (!hasReply && !instructionElement.value) {
    clearCachedReply(currentItemKey);
    return;
  }

  const state: CachedReplyState = {
    instruction: instructionElement.value,
    hasReply,
    replyText: textElement.value,
    originalAiReply,
    lastSentEditedReply,
    isReplyEditable: !textElement.readOnly,
    replyFeedback,
    feedbackComment: commentElement.value,
  };
  saveCachedReply(currentItemKey, state);
}

function scheduleInstructionCachePersist(): void {
  if (instructionCachePersistTimer !== undefined) {
    window.clearTimeout(instructionCachePersistTimer);
  }
  instructionCachePersistTimer = window.setTimeout(persistReplyCache, 500);
}

function restoreCachedReply(cached: CachedReplyState): void {
  const textElement = document.getElementById("reply-text") as HTMLTextAreaElement;
  const instructionElement = document.getElementById("reply-instruction") as HTMLTextAreaElement;
  const commentElement = document.getElementById("reply-feedback-comment") as HTMLTextAreaElement;

  instructionElement.value = cached.instruction;
  autoResizeInstruction();

  if (!cached.hasReply) {
    return;
  }

  textElement.value = cached.replyText;
  renderReplyHighlight();
  originalAiReply = cached.originalAiReply;
  lastSentEditedReply = cached.lastSentEditedReply;
  setReplyEditable(cached.isReplyEditable);
  document.getElementById("insert-reply").classList.remove("is-hidden");
  document.getElementById("reply-feedback").classList.remove("is-hidden");

  if (cached.replyFeedback) {
    replyFeedback = cached.replyFeedback;
    document.getElementById("feedback-up").classList.toggle("is-selected", cached.replyFeedback === "up");
    document.getElementById("feedback-down").classList.toggle("is-selected", cached.replyFeedback === "down");
    document.getElementById("reply-feedback-submit").classList.remove("is-disabled");
  }
  commentElement.value = cached.feedbackComment;
}

function setGenerateButtonLoading(loading: boolean): void {
  const generateButton = document.getElementById("generate-reply");
  generateButton.classList.toggle("is-loading", loading);
  generateButton.setAttribute("aria-label", loading ? "Annuler la génération" : "Envoyer");
  generateButton.title = loading ? "Annuler la génération" : "Envoyer";
}

function handleCancelGenerate(): void {
  generationAbortController?.abort();
}

function handleGenerateButtonClick(): void {
  if (isDictationRecording()) {
    handleDictateInstruction();
    return;
  }
  if (isGeneratingReply) {
    handleCancelGenerate();
    return;
  }
  void handleGenerateReply();
}

async function handleGenerateReply(): Promise<void> {
  if (isGeneratingReply) {
    return;
  }

  const statusElement = document.getElementById("reply-status");
  const textElement = document.getElementById("reply-text") as HTMLTextAreaElement;
  const instructionElement = document.getElementById("reply-instruction") as HTMLTextAreaElement;
  const generateButton = document.getElementById("generate-reply");
  const insertButton = document.getElementById("insert-reply");

  const consigneText = instructionElement.value;

  isGeneratingReply = true;
  generationAbortController = new AbortController();
  const myGenerationId = ++generationId;
  setGenerateButtonLoading(true);
  statusElement.textContent = "";
  statusElement.className = "reply-status";
  insertButton.classList.add("is-hidden");
  document.getElementById("reply-feedback").classList.add("is-hidden");
  resetReplyFeedback();
  originalAiReply = null;
  lastSentEditedReply = null;
  currentModule = null;
  currentVersionModule = null;
  instructionElement.value = "";
  autoResizeInstruction();

  try {
    if (!mailData.graphToken) {
      throw new Error("Session Microsoft expirée, rechargez le module.");
    }
    const attachmentsForRequest = mailData.attachments
      .filter((attachment) => attachment.included)
      .map((attachment) => ({
        filename: attachment.filename,
        text: attachment.text || "",
        erreur: attachment.erreur,
      }));
    const onSlowResponse = () => {
      statusElement.textContent =
        "Nous vous remercions de patienter durant la génération de la réponse... Votre demande nécessite peut-être de nombreuses ressources, ou il se peut que nos serveurs soient potentiellement sollicités par de nombreuses demandes en ce moment même.";
      statusElement.className = "reply-status reply-status--pending";
    };
    const reply = await generateReply(
      mailData.subject,
      buildReplyThread(),
      consigneText,
      textElement.value.trim(),
      attachmentsForRequest,
      mailData.graphToken,
      onSlowResponse,
      generationAbortController.signal
    );
    textElement.value = reply;
    renderReplyHighlight();
    originalAiReply = reply;
    setReplyEditable(false);
    statusElement.textContent = "";
    insertButton.classList.remove("is-hidden");
    document.getElementById("reply-feedback").classList.remove("is-hidden");
    persistReplyCache();
  } catch (error) {
    if (error instanceof PraetoriaTimeoutError) {
      statusElement.textContent = "Le service est actuellement en maintenance, merci de réessayer plus tard.";
      statusElement.className = "reply-status reply-status--error";
    } else if (error instanceof PraetoriaCancelledError) {
      statusElement.textContent = "Génération annulée.";
      statusElement.className = "reply-status";
    } else if (error instanceof ServiceUnavailableError) {
      statusElement.textContent = error.message;
      statusElement.className = "reply-status reply-status--error";
    } else {
      statusElement.textContent = `Impossible de générer une réponse (${errorMessage(error)}).`;
      statusElement.className = "reply-status reply-status--error";
    }
  } finally {
    // Si une génération plus récente (ou un Recharger) a démarré entre-temps, ne pas écraser
    // son état -- voir le commentaire sur `generationId` plus haut.
    if (myGenerationId === generationId) {
      setGenerateButtonLoading(false);
      generationAbortController = null;
      isGeneratingReply = false;
    }
  }
}

function textToHtml(text: string): string {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\r\n|\r|\n/g, "<br>");
  return `<span style="font-family: Calibri, sans-serif; font-size: 11pt;">${escaped}</span>`;
}

function resetReplyFeedback(): void {
  replyFeedback = null;
  isSubmittingFeedback = false;
  document.getElementById("feedback-up").classList.remove("is-selected", "is-disabled");
  document.getElementById("feedback-down").classList.remove("is-selected", "is-disabled");
  (document.getElementById("reply-feedback-comment") as HTMLTextAreaElement).value = "";
  document.getElementById("reply-feedback-submit").classList.add("is-disabled");
  const statusElement = document.getElementById("reply-feedback-status");
  statusElement.textContent = "";
  statusElement.className = "reply-status";
}

function handleReplyFeedback(value: "up" | "down"): void {
  if (isSubmittingFeedback) {
    return;
  }
  replyFeedback = value;
  document.getElementById("feedback-up").classList.toggle("is-selected", value === "up");
  document.getElementById("feedback-down").classList.toggle("is-selected", value === "down");
  document.getElementById("reply-feedback-submit").classList.remove("is-disabled");
  const statusElement = document.getElementById("reply-feedback-status");
  statusElement.textContent = "";
  statusElement.className = "reply-status";
  persistReplyCache();
}

async function handleSubmitReplyFeedback(): Promise<void> {
  if (isSubmittingFeedback || !replyFeedback) {
    return;
  }
  if (!mailData.graphToken) {
    return;
  }

  const upButton = document.getElementById("feedback-up");
  const downButton = document.getElementById("feedback-down");
  const submitButton = document.getElementById("reply-feedback-submit");
  const statusElement = document.getElementById("reply-feedback-status");
  const commentElement = document.getElementById("reply-feedback-comment") as HTMLTextAreaElement;

  isSubmittingFeedback = true;
  upButton.classList.add("is-disabled");
  downButton.classList.add("is-disabled");
  submitButton.classList.add("is-disabled");
  statusElement.textContent = "Envoi de votre retour...";
  statusElement.className = "reply-status";

  try {
    await submitUserReview(
      mailData.graphToken,
      replyFeedback === "up" ? "ok" : "ko",
      mailData.subject || "(sans objet)",
      commentElement.value.trim(),
      currentModule,
      currentVersionModule
    );
    document.getElementById("reply-feedback").classList.add("is-hidden");
    statusElement.textContent = "";
    statusElement.className = "reply-status";
  } catch (error) {
    statusElement.textContent = describeActionError("Impossible d'enregistrer votre retour", error);
    statusElement.className = "reply-status reply-status--error";
  } finally {
    isSubmittingFeedback = false;
    upButton.classList.remove("is-disabled");
    downButton.classList.remove("is-disabled");
    submitButton.classList.remove("is-disabled");
    persistReplyCache();
  }
}

function extractBracketedNotices(text: string): string[] {
  const notices: string[] = [];
  const pattern = /\[([^[\]]+)\]/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    notices.push(match[0]);
  }
  return notices;
}

function insertReplyIntoMail(): void {
  const textElement = document.getElementById("reply-text") as HTMLTextAreaElement;
  const item = Office.context.mailbox.item as Office.MessageRead;
  // Filet de sécurité : sur certains clients (manifest XML legacy, panneau épinglé), Office.js ne
  // détruit pas forcément le taskpane quand l'utilisateur change de mail -- sans ce contrôle, une
  // réponse générée pour un mail pourrait s'insérer en Reply-All sur un autre.
  if (currentItemKey && item.itemId && item.itemId !== currentItemKey) {
    const statusElement = document.getElementById("reply-status");
    statusElement.textContent =
      "Le mail affiché a changé depuis la génération de cette réponse. Cliquez sur Recharger avant d'insérer, pour ne pas l'envoyer au mauvais destinataire.";
    statusElement.className = "reply-status reply-status--error";
    return;
  }
  void reportReplyEditIfNeeded();
  // Reply All (pas Reply) pour que le brouillon reprenne automatiquement les CC du mail source --
  // displayReplyForm ne permet pas de spécifier des destinataires, seul Outlook sait reconstruire
  // la liste To/CC d'origine.
  item.displayReplyAllForm(textToHtml(textElement.value));
}

function hideBracketWarning(): void {
  document.getElementById("bracket-warning-overlay").classList.add("is-hidden");
}

function hideInfoPopup(): void {
  document.getElementById("info-popup-overlay").classList.add("is-hidden");
}

function showInfoPopup(message: string): void {
  document.getElementById("info-popup-message").textContent = message;
  document.getElementById("info-popup-overlay").classList.remove("is-hidden");
}

function showBracketWarning(notices: string[]): void {
  const detail =
    notices.length === 1
      ? `« ${notices[0]} » requiert votre attention avant l'envoi.`
      : `Ces éléments requièrent votre attention avant l'envoi : ${notices.join(", ")}.`;
  document.getElementById("bracket-warning-message").textContent =
    `${detail}\n\nCes mentions s'adressent à vous, pas au destinataire.`;
  document.getElementById("bracket-warning-overlay").classList.remove("is-hidden");
}

function handleInsertReply(): void {
  const textElement = document.getElementById("reply-text") as HTMLTextAreaElement;
  const notices = extractBracketedNotices(textElement.value);
  if (notices.length > 0) {
    showBracketWarning(notices);
    return;
  }
  insertReplyIntoMail();
}

function handleClearReply(): void {
  const textElement = document.getElementById("reply-text") as HTMLTextAreaElement;
  const statusElement = document.getElementById("reply-status");
  textElement.value = "";
  renderReplyHighlight();
  statusElement.textContent = "";
  statusElement.className = "reply-status";
  document.getElementById("insert-reply").classList.add("is-hidden");
  document.getElementById("reply-feedback").classList.add("is-hidden");
  resetReplyFeedback();
  originalAiReply = null;
  lastSentEditedReply = null;
  setReplyEditable(false);
  persistReplyCache();
}

function setReplyEditable(editable: boolean): void {
  const textElement = document.getElementById("reply-text") as HTMLTextAreaElement;
  const wrapElement = document.getElementById("reply-text-wrap");
  const editButton = document.getElementById("edit-reply");
  textElement.readOnly = !editable;
  wrapElement.classList.toggle("is-readonly", !editable);
  editButton.classList.toggle("is-active", editable);
  editButton.setAttribute("aria-label", editable ? "Verrouiller la réponse" : "Modifier la réponse");
  editButton.title = editable ? "Verrouiller la réponse" : "Modifier la réponse";
}

function handleToggleEditReply(): void {
  const textElement = document.getElementById("reply-text") as HTMLTextAreaElement;
  const wasReadOnly = textElement.readOnly;
  setReplyEditable(wasReadOnly);
  if (wasReadOnly) {
    textElement.focus();
  }
}

export async function displayMailContent(): Promise<void> {
  // Sauvegarde l'état courant (consigne, réponse, commentaire de retour non envoyé) avant le
  // resetReplyPanel() plus bas, pour que Recharger le restaure via le cache au lieu de le perdre.
  persistReplyCache();

  const item = Office.context.mailbox.item as Office.MessageRead;
  currentItemKey = item.itemId || null;

  // Le token Graph n'est acquis qu'une fois au démarrage (runLicenseCheck) et expire au bout
  // d'environ 1h -- Recharger le renouvelle (MSAL rafraîchit silencieusement s'il est expiré).
  // En cas d'échec on garde l'ancien plutôt que de le remplacer par undefined.
  const [subject, from, refreshedToken] = await Promise.all([
    resolveSubjectText(item),
    resolveFromText(item),
    mailData.graphToken ? acquireGraphToken() : Promise.resolve(undefined),
  ]);
  if (refreshedToken) {
    mailData.graphToken = refreshedToken;
  }

  setMailData({
    subject,
    from,
    date: item.dateTimeCreated ? item.dateTimeCreated.toISOString() : "",
    body: "",
    thread: [],
    attachments: [],
    userEmail: mailData.userEmail,
    graphToken: mailData.graphToken,
  });

  resetReplyPanel();

  const cached = currentItemKey ? loadCachedReply(currentItemKey) : null;
  if (cached) {
    restoreCachedReply(cached);
  }

  try {
    mailData.body = await getBodyText(item);
  } catch (error) {
    console.error("Impossible de lire le contenu du mail", error);
  }

  await loadThread(item);
  void loadAttachments();
}
